import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import JuntoApiService, { Location } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const LocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    instagram: '',
  });

  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getLocations(includeInactive);
      setLocations(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const openCreateDialog = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      email: '',
      instagram: '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      phone: location.phone || '',
      email: location.email || '',
      instagram: location.instagram || '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingLocation(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const locationData = {
        ...formData,
        id: editingLocation?.id || `location-${Date.now()}`,
        created_at: editingLocation?.created_at || new Date().toISOString(),
      };

      if (editingLocation) {
        // Update existing location
        await JuntoApiService.updateLocation(locationData as Location);
      } else {
        // Create new location
        await JuntoApiService.createLocation(locationData);
      }
      
      await loadLocations();
      closeDialog();
    } catch (err) {
      console.error('Failed to save location:', err);
      setDialogError('Failed to save location. Please check your input.');
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading locations...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Locations</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Location
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadLocations} sx={{ ml: 2 }}>Retry</Button>
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Instagram</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.map((location) => (
                <TableRow 
                  key={location.id} 
                  hover 
                  sx={{ 
                    opacity: location.is_active ? 1 : 0.6,
                    backgroundColor: location.is_active ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <LocationIcon color={location.is_active ? "primary" : "disabled"} sx={{ mr: 1 }} />
                      <Typography variant="body1" fontWeight="medium">
                        {location.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{location.address}</TableCell>
                  <TableCell>
                    {location.phone ? (
                      <Box display="flex" alignItems="center">
                        <PhoneIcon color="action" sx={{ mr: 1 }} />
                        {location.phone}
                      </Box>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {location.email ? (
                      <Box display="flex" alignItems="center">
                        <EmailIcon color="action" sx={{ mr: 1 }} />
                        {location.email}
                      </Box>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {location.instagram ? (
                      <Box display="flex" alignItems="center">
                        <InstagramIcon color="action" sx={{ mr: 1 }} />
                        {location.instagram}
                      </Box>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={location.is_active ? "Active" : "Inactive"}
                      color={location.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={location}
                      entityType="locations"
                      entityDisplayName="Location"
                      onEdit={openEditDialog}
                      onRefresh={loadLocations}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Address:</strong> {entity.address}
                          </Typography>
                          {entity.phone && (
                            <Typography variant="body1">
                              <strong>Phone:</strong> {entity.phone}
                            </Typography>
                          )}
                          {entity.email && (
                            <Typography variant="body1">
                              <strong>Email:</strong> {entity.email}
                            </Typography>
                          )}
                          {entity.instagram && (
                            <Typography variant="body1">
                              <strong>Instagram:</strong> {entity.instagram}
                            </Typography>
                          )}
                          <Typography variant="body1">
                            <strong>Active:</strong> {entity.is_active ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLocation ? 'Edit Location' : 'Create New Location'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="info@location.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Instagram"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="@locationname"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingLocation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationsPage;